import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Badge,
  Tooltip,
  Divider,
} from '@mui/material';
import {
  Security as SecurityIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Assessment as AssessmentIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  Gavel as GavelIcon,
  Shield as ShieldIcon,
  LocalShipping as ShippingIcon,
  Psychology as AIIcon,
  AutoAwesome as AutoAwesomeIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { brand } from '../theme';
import SanctionsScreener from '../components/Compliance/SanctionsScreener';
import RiskMeter from '../components/Compliance/RiskMeter';
import ComplianceReport from '../components/Compliance/ComplianceReport';
import AIComplianceChecker from '../components/Compliance/AIComplianceChecker';
import RiskAssessmentVisualizer from '../components/Compliance/RiskAssessmentVisualizer';
import complianceService from '../services/complianceService';
import { useTranslation } from '../store/languageStore';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`compliance-tabpanel-${index}`}
      aria-labelledby={`compliance-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const ComplianceDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { t, tString } = useTranslation();
  const [tabValue, setTabValue] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [complianceStats, setComplianceStats] = useState({
    totalChecks: 0,
    compliant: 0,
    nonCompliant: 0,
    pending: 0,
    avgRiskScore: 0,
  });
  const [recentReports, setRecentReports] = useState<any[]>([]);
  const [regulationUpdates, setRegulationUpdates] = useState<any[]>([]);
  const [selectedReport, setSelectedReport] = useState<any>(null);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    setIsLoading(true);
    try {
      // Load compliance history
      const history = await complianceService.getComplianceHistory(5, 0);
      setRecentReports(history);

      // Calculate stats
      const stats = {
        totalChecks: history.length,
        compliant: history.filter((r: any) => r.overall_status === 'compliant').length,
        nonCompliant: history.filter((r: any) => r.overall_status === 'non_compliant').length,
        pending: history.filter((r: any) => r.overall_status === 'pending').length,
        avgRiskScore: history.reduce((acc: number, r: any) => acc + r.risk_score, 0) / history.length || 0,
      };
      setComplianceStats(stats);

      // Load regulation updates
      const updates = await complianceService.checkRegulationUpdates();
      setRegulationUpdates(updates.slice(0, 3));
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'compliant':
        return <CheckIcon color="success" />;
      case 'non_compliant':
        return <ErrorIcon color="error" />;
      case 'requires_review':
        return <WarningIcon color="warning" />;
      default:
        return <SecurityIcon color="action" />;
    }
  };

  const getRiskColor = (score: number) => {
    if (score >= 80) return 'error';
    if (score >= 60) return 'warning';
    if (score >= 40) return 'info';
    return 'success';
  };

  return (
    <Box sx={{ height: '100%', bgcolor: 'grey.50', overflow: 'auto' }}>
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>

      {/* Page Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, color: brand.textPrimary }}>
          <ShieldIcon sx={{ fontSize: 36, color: brand.orange }} />
          BEO Sanctions Compliance Dashboard
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Screen entities across 11 global sanctions lists including BE, EU, UN, OFAC SDN, AUCL, and more.
          Powered by BEO Sanctions API with real-time updates and AI-enhanced analysis.
        </Typography>
      </Box>

      {/* Stats Cards — clean white background with a brand-colored accent
          bar so each KPI carries semantic meaning rather than just a
          decorative gradient. Pattern mirrors DocumentProcessing cards. */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {[
          {
            label: 'Total Checks',
            value: complianceStats.totalChecks,
            caption: 'All time compliance checks',
            accent: brand.orange,
          },
          {
            label: 'Compliant',
            value: complianceStats.compliant,
            caption: complianceStats.totalChecks > 0
              ? `${((complianceStats.compliant / complianceStats.totalChecks) * 100).toFixed(0)}% compliance rate`
              : 'No checks yet',
            accent: brand.success,
          },
          {
            label: 'Non-Compliant',
            value: complianceStats.nonCompliant,
            caption: 'Requires immediate action',
            accent: brand.error,
          },
          {
            label: 'BEO Lists Active',
            value: 11,
            caption: 'BE, EU, SDN, UN, AUCL, DPL, EL, CA, JPN, IN, UFL',
            accent: brand.orange,
            icon: <ShieldIcon sx={{ fontSize: 18 }} />,
          },
        ].map((stat) => (
          <Grid key={stat.label} item xs={12} sm={6} md={3}>
            <Card
              elevation={1}
              sx={{
                bgcolor: '#fff',
                borderRadius: 2,
                borderLeft: `4px solid ${stat.accent}`,
                height: '100%',
              }}
            >
              <CardContent>
                <Typography
                  variant="overline"
                  sx={{ color: brand.textSecondary, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.75 }}
                  gutterBottom
                >
                  {stat.icon}
                  {stat.label}
                </Typography>
                <Typography variant="h3" sx={{ color: stat.accent, fontWeight: 700, lineHeight: 1.1, mb: 0.5 }}>
                  {stat.value}
                </Typography>
                <Typography variant="caption" sx={{ color: brand.textSecondary }}>
                  {stat.caption}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Main Content Tabs */}
      <Paper elevation={1} sx={{ width: '100%', mb: 3, borderRadius: 2, overflow: 'hidden' }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            borderBottom: `1px solid ${brand.borderSubtle}`,
            px: 2,
            '& .MuiTabs-indicator': { backgroundColor: brand.orange },
            '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 },
            '& .Mui-selected': { color: `${brand.orange} !important` },
          }}
        >
          <Tab
            icon={<Badge badgeContent="BEO" color="error"><SecurityIcon /></Badge>}
            label="Sanctions Screening"
            iconPosition="start"
          />
          <Tab
            icon={<AssessmentIcon />}
            label="Reports"
            iconPosition="start"
          />
          <Tab
            icon={<Badge badgeContent="AI" color="secondary"><AIIcon /></Badge>}
            label="AI-Powered Analysis"
            iconPosition="start"
          />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          <SanctionsScreener />
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              {selectedReport ? (
                <ComplianceReport report={selectedReport} />
              ) : (
                <Alert severity="info">
                  Select a report from the list or run a new compliance check
                </Alert>
              )}
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Recent Reports
                </Typography>
                <Divider sx={{ mb: 2 }} />
                {isLoading ? (
                  <CircularProgress />
                ) : recentReports.length > 0 ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {recentReports.map((report: any) => (
                      <Paper
                        key={report.report_id}
                        sx={{
                          p: 2,
                          cursor: 'pointer',
                          '&:hover': { bgcolor: 'action.hover' },
                          border: selectedReport?.report_id === report.report_id ? `2px solid ${brand.orange}` : `1px solid ${brand.borderSubtle}`,
                        }}
                        onClick={() => setSelectedReport(report)}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          {getStatusIcon(report.overall_status)}
                          <Typography variant="subtitle2">
                            {report.report_id}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                          <Chip
                            size="small"
                            label={report.risk_level}
                            color={getRiskColor(report.risk_score)}
                          />
                          <Chip
                            size="small"
                            label={`Score: ${report.risk_score}`}
                            variant="outlined"
                          />
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(report.created_at).toLocaleDateString()}
                        </Typography>
                      </Paper>
                    ))}
                  </Box>
                ) : (
                  <Alert severity="info">No reports yet</Alert>
                )}
              </Paper>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <Grid container spacing={3}>
            {/* AI Features Overview */}
            <Grid item xs={12}>
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AutoAwesomeIcon />
                  AI-Powered Compliance Engine
                </Typography>
                <Typography variant="body2">
                  Leverage advanced AI capabilities powered by Mistral models for intelligent document processing,
                  entity extraction, semantic matching, and risk assessment. All AI features are designed to enhance
                  accuracy and reduce manual effort in compliance checking.
                </Typography>
              </Alert>
            </Grid>

            {/* AI Compliance Checker */}
            <Grid item xs={12}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                  <AIIcon color="primary" />
                  AI Document Compliance Checker
                </Typography>
                <AIComplianceChecker />
              </Paper>
            </Grid>

            {/* Risk Assessment Visualization */}
            {selectedReport && selectedReport.risk_assessment && (
              <Grid item xs={12}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                    <AssessmentIcon color="primary" />
                    AI Risk Assessment Visualization
                  </Typography>
                  <RiskAssessmentVisualizer 
                    riskAssessment={selectedReport.risk_assessment}
                    aiEnhanced={true}
                    showDetails={true}
                  />
                </Paper>
              </Grid>
            )}

            {/* AI Capabilities — same clean-card treatment as the KPI row;
                brand-orange icon + neutral typography across all four. */}
            <Grid item xs={12}>
              <Grid container spacing={2}>
                {[
                  {
                    title: 'OCR Processing',
                    body: 'Extract text from documents using mistral-ocr-latest model with 99%+ accuracy',
                  },
                  {
                    title: 'Entity Extraction',
                    body: 'Automatically identify companies, individuals, products, and locations',
                  },
                  {
                    title: 'Semantic Matching',
                    body: 'Match entities based on meaning, not just text similarity',
                  },
                  {
                    title: 'Risk Assessment',
                    body: 'AI-powered risk scoring with detailed explanations and mitigations',
                  },
                ].map((cap) => (
                  <Grid key={cap.title} item xs={12} md={6} lg={3}>
                    <Card
                      elevation={1}
                      sx={{
                        height: '100%',
                        bgcolor: '#fff',
                        borderRadius: 2,
                        border: `1px solid ${brand.borderSubtle}`,
                      }}
                    >
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                          <AutoAwesomeIcon sx={{ color: brand.orange }} />
                          <Typography variant="h6" sx={{ color: brand.textPrimary, fontWeight: 600 }}>
                            {cap.title}
                          </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ color: brand.textSecondary }}>
                          {cap.body}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Grid>

            {/* AI Models Info */}
            <Grid item xs={12}>
              <Paper elevation={0} sx={{ p: 3, bgcolor: brand.dropZoneBg, border: `1px solid ${brand.borderSubtle}`, borderRadius: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Powered by Mistral AI Models
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip label="mistral-ocr-latest" color="primary" />
                      <Typography variant="body2">Document OCR & Processing</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip label="mistral-large-latest" color="secondary" />
                      <Typography variant="body2">Entity Extraction & Analysis</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip label="mistral-embed" color="success" />
                      <Typography variant="body2">Semantic Similarity Matching</Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        </TabPanel>
      </Paper>

      {/* Regulation Updates */}
      {regulationUpdates.length > 0 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Badge badgeContent={regulationUpdates.length} color="error">
              <GavelIcon />
            </Badge>
            Recent Regulation Updates
          </Typography>
          <Grid container spacing={2}>
            {regulationUpdates.map((update: any, index: number) => (
              <Grid item xs={12} md={4} key={index}>
                <Alert 
                  severity="warning" 
                  action={
                    <Button size="small" onClick={() => setTabValue(3)}>
                      VIEW
                    </Button>
                  }
                >
                  <Typography variant="subtitle2">
                    {update.jurisdiction} - {update.code}
                  </Typography>
                  <Typography variant="body2">
                    {update.name}
                  </Typography>
                  <Typography variant="caption">
                    Updated: {update.updated}
                  </Typography>
                </Alert>
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}

      {/* Risk Overview */}
      <Grid container spacing={3} sx={{ mt: 2 }}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Risk Overview
            </Typography>
            <RiskMeter score={complianceStats.avgRiskScore} />
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Quick Actions
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Button
                variant="contained"
                startIcon={<SecurityIcon />}
                onClick={() => setTabValue(0)}
                fullWidth
                sx={{
                  backgroundColor: brand.black,
                  color: '#fff',
                  py: 1.2,
                  fontWeight: 600,
                  borderRadius: 1,
                  textTransform: 'none',
                  '&:hover': { backgroundColor: '#000' },
                }}
              >
                Run Compliance Check
              </Button>
              <Button
                variant="outlined"
                startIcon={<SearchIcon />}
                onClick={() => setTabValue(3)}
                fullWidth
              >
                Search Regulations
              </Button>
              <Button
                variant="outlined"
                startIcon={<ShippingIcon />}
                onClick={() => navigate('/process')}
                fullWidth
              >
                Process New Document
              </Button>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={loadDashboardData}
                fullWidth
              >
                Refresh Dashboard
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  </Box>
  );
};

export default ComplianceDashboard;